import React from 'react';

import { i18n } from 'config/locales';
import { countryValueResolver } from 'helpers/location';

import { LocationModel } from 'forms/shared/types';
import { CommonAddressBooks, CommonOnError, CommonOnUpdate } from 'types/props';
import {
  createLocationModelObjectFromProps,
  getCountryValue,
  isSafeInternational,
  isSafePostOffice,
  isSafeUnitedStates,
} from 'helpers/locations';
import { COUNTRY_FOREIGN } from 'constants/enums/locations';
import LocationValidator, { countryString } from '../../../validators/location';
import ValidationElement from '../ValidationElement';
import Street from '../Street';
import State from '../State';
import City from '../City';
import Country from '../Country';
import ZipCode from '../ZipCode';
import RadioGroup from '../RadioGroup';
import Radio from '../Radio';
import ApoFpo from '../ApoFpo';
import Suggestions from '../Suggestions';
import { AddressSuggestion } from './AddressSuggestion';
import { setAttribute } from '../shared/setAttribute';
import { ValidationElementProps } from '../ValidationElement/ValidationElement';
import SvgComponent from '../SvgComponent';

interface Props extends ValidationElementProps, Partial<LocationModel> {
  name?: string
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  onFocus?: any
  isEnabled?: boolean
  addressBook?: string
  addressBooks?: CommonAddressBooks
  disableToggle?: boolean
  label?: string
  showPostOffice?: boolean
  disabled?: boolean
  attribute?: string
  streetLabel?: string
  street2Label?: string
  cityLabel?: string
  stateLabel?: string
  zipcodeLabel?: string
  countryLabel?: string
  postOfficeStreetPlaceholder?: string
  postOfficeStateLabel?: string
  postOfficeZipcodeLabel?: string
  streetPlaceholder?: string
  required?: boolean
  isPoBoxAllowed?: boolean
  useChangeWarning: boolean
  confirmationWindowText: string
}

interface AddressState {
  uid: string
  showAddressBook: boolean
}

export default class Address extends ValidationElement<Props, AddressState> {
  constructor(props: Props) {
    super(props);

    this.state = {
      uid: `${this.props.name}-${super.guid()}`,
      showAddressBook: false,
    };

    this.blurred = {
      street: true,
      street2: true,
      city: true,
      state: true,
      country: true,
      zipcode: true,
    };

    this.apoZipcode = React.createRef();

    this.errors = [];
  }

  onAddressUpdate = (nextValue) => {
    const { name, value } = nextValue;

    this.update({
      [name]: value,
    });
  };

  updateCountry = (values) => {
    this.update({
      country: { value: values.value ? values.value : '' },
      countryComments: values.comments,
    });
  };

  updateAddressType = (values) => {
    // Check if data is entered in fields being cleared, prompt the user
    // for confirmation if so before deleting any data
    if (this.props.useChangeWarning === true) {
      if (this.props.city
        || this.props.state
        || this.props.street
        || this.props.zipcode
        || (
          this.props.country.value
          && this.props.country.value !== 'United States'
          && this.props.country.value !== 'INTERNATIONAL'
        )
      ) {
        if (window?.confirm(this.props.confirmationWindowText) === false) {
          return;
        }
      }
    }

    // Set existing errors to null when toggling fields
    this.props.onError(
      values.value,
      this.errors.map((err) => ({
        code: err.code,
        valid: null,
        uid: err.uid,
      }))
    );

    const country = { value: '' };

    // POSTOFFICE is used for APO, FPO and DPO (DPO addresses are not permitted by eQip)
    if (!isSafeInternational(values.value)) {
      country.value = values.value;
      this.blurred = {
        street: true,
        street2: true,
        city: true,
        state: true,
        country: true,
        zipcode: true,
      };
    } else {
      country.value = COUNTRY_FOREIGN;
    }

    this.update({
      country,
      city: '',
      state: '',
      zipcode: '',
      street: '',
      street2: '',
    });
  };

  update = (values, delay = null, blur = false) => {
    // Get the next values
    const next = {
      street: this.props.street,
      street2: this.props.street2,
      city: this.props.city,
      state: this.props.state,
      country: this.props.country,
      zipcode: this.props.zipcode,
      validated: this.props.validated,
      ...values,
    };

    // Determine if previous values were the same
    const same = next.street === this.props.street
      && next.street2 === this.props.street2
      && next.city === this.props.city
      && next.state === this.props.state
      && getCountryValue(next.country) === getCountryValue(this.props.country)
      && next.zipcode === this.props.zipcode;

    // If it is not the same then we need to force validation
    next.validated = same && this.props.validated;

    // Update the properties
    if (!same || blur) {
      this.props.onUpdate(next, delay);
    }
  };

  blurForceUpdate = (values) => {
    const blurry = Object.values(this.blurred).every((value) => !!value);
    const modal = document.querySelector('.modal');
    let changed = false;
    for (const name in values) {
      if (this.props?.[name] !== values[name]) {
        changed = true;
      }
    }

    if (blurry && !modal && (!this.props.validated || changed)) {
      this.update(values, 500, true);
    }
  };

  updateInternationalField = ({ name, value }) => {
    if (name) {
      const values = { [name]: value };
      this.props.onUpdate({
        street: this.props.street,
        street2: this.props.street2,
        city: this.props.city,
        country: this.props.country,
        ...values
      });
    }
  };

  focusField = (event) => {
    const field = event.target.name;

    this.props.onFocus(event);
    this.blurred[field] = false;
  };

  blurField = (event) => {
    const { name, value } = event.target;

    this.blurred[name] = true;
    this.blurForceUpdate({ [name]: value });
    super.handleBlur(event);
  };

  addressType = () => {
    // If address is not applicable ("I don't know"), return ""
    if (!this.props.isEnabled) {
      return '';
    }
    const locationObject = createLocationModelObjectFromProps(this.props);

    if (isSafeUnitedStates(locationObject, false)) { return 'United States'; }
    if (isSafePostOffice(locationObject, false)) { return 'POSTOFFICE'; }
    if (isSafeInternational(locationObject, false)) { return 'International'; }

    // default to US if no country provided
    return 'United States';
  };

  openAddressBook = () => this.setState({ showAddressBook: true });

  closeAddressBook = () => this.setState({ showAddressBook: false });

  renderAddressBookItem = (suggestion) => (
    <AddressSuggestion suggestion={suggestion} current={suggestion} />
  );

  selectAddressBookItem = (suggestion) => {
    this.setState({ showAddressBook: false }, () => {
      this.update({
        street: suggestion.street,
        street2: suggestion.street2,
        city: suggestion.city,
        state: suggestion.state,
        zipcode: suggestion.zipcode,
        county: suggestion.county,
        country: suggestion.country,
        validated: true,
      });
    });
  };

  handleError = (value, arr) => {
    // eslint-disable-next-line no-param-reassign
    arr = arr.map((err) => ({
      code: `address.${err.code}`,
      valid: err.valid,
      uid: err.uid,
    }));

    const requiredErr = arr.concat(
      this.constructor.errors.map((err) => ({
        code: `address.${err.code}`,
        valid: err.func(value, { ...this.props }),
        uid: this.state.uid,
      }))
    );

    this.storeErrors(requiredErr);
    this.props.onError(value, requiredErr);
    return arr;
  };

  storeErrors = (errors) => {
    const newErrors = [...errors];

    for (let i = 0; i < newErrors.length; i += 1) {
      const e = newErrors[i];
      const idx = this.errors.findIndex((x) => x.uid === e.uid && x.code === e.code);
      if (idx !== -1) {
        this.errors[idx] = { ...e };
      } else {
        this.errors.push({ ...e });
      }
    }
  };

  filteredAddressBook(book) {
    const {
      city, country, state, street, zipcode
    } = this.props;
    return book.filter(
      (address) => address.city !== city
        || address.country !== country
        || address.state !== state
        || address.street !== street
        || address.zipcode !== zipcode
    );
  }

  render() {
    const book = this.filteredAddressBook(this.props.addressBooks[this.props.addressBook] || []);
    const locationValidator = new LocationValidator(this.props);
    const instateZipcode = locationValidator.validZipcodeState();
    const locationObject = createLocationModelObjectFromProps(this.props);

    return (
      <div className="address">
        {!this.props.disableToggle && (
          <>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label id="address">{this.props.label}</label>
            <RadioGroup
              className={`address-options option-list ${this.props.showPostOffice ? '' : 'no-postoffice'
              }`.trim()}
              disabled={this.props.disabled}
              selectedValue={this.addressType()}
            >
              <Radio
                name="addressType"
                label={i18n.t('address.options.us.label')}
                value="United States"
                className="domestic"
                ignoreDeselect
                disabled={this.props.disabled}
                onUpdate={this.updateAddressType}
                onFocus={this.props.onFocus}
              />
              {this.props.showPostOffice && (
                <Radio
                  name="addressType"
                  label={i18n.t('address.options.apoFpo.label')}
                  value="POSTOFFICE"
                  className="apofpo postoffice"
                  ignoreDeselect
                  disabled={this.props.disabled}
                  onUpdate={this.updateAddressType}
                  onFocus={this.props.onFocus}
                />
              )}
              <Radio
                name="addressType"
                label={i18n.t('address.options.international.label')}
                value="International"
                className="international"
                ignoreDeselect
                disabled={this.props.disabled}
                onUpdate={this.updateAddressType}
                onFocus={this.props.onFocus}
              />
            </RadioGroup>
          </>
        )}

        {book.length > 0 && (
          <div className="reuse-address">
            <button
              type="button"
              className="reuse-address-open-modal"
              title={i18n.t('address.addressBook.reuse')}
              onClick={this.openAddressBook}
            >
              <div className="address-book" aria-hidden="true">
                <SvgComponent className="address-book" src="/img/address-book.svg" />
              </div>
              <span>{i18n.t('address.addressBook.reuse')}</span>
            </button>
          </div>
        )}

        <Suggestions
          show={this.state.showAddressBook}
          suggestions={book}
          renderSuggestion={this.renderAddressBookItem}
          suggestionTitle={i18n.t('suggestions.addressBook.title')}
          suggestionParagraph={book.length > 0 ? i18n.m('suggestions.addressBook.para') : ''}
          suggestionLabel={i18n.t('suggestions.addressBook.label')}
          suggestionUseLabel={i18n.t('suggestions.addressBook.use')}
          suggestionDismissLabel={i18n.t('suggestions.addressBook.dismiss')}
          onSuggestion={this.selectAddressBookItem}
          onDismiss={this.closeAddressBook}
        />

        <div className="fields">
          {this.props.isEnabled && isSafeUnitedStates(locationObject, false) && (
            <div>
              <div className="usa-form-control">
                <Street
                  attribute={setAttribute(this.props.attribute, 'street')}
                  name="street"
                  className="mailing street required"
                  label={this.props.streetLabel}
                  placeholder={this.props.streetPlaceholder}
                  value={this.props.street}
                  onUpdate={this.onAddressUpdate}
                  onError={this.handleError}
                  onFocus={this.focusField}
                  onBlur={this.blurField}
                  required={this.props.required}
                  disabled={this.props.disabled}
                  isPoBoxAllowed={this.props.isPoBoxAllowed}
                  showDisabledText={this.props.showDisabledText}
                />
              </div>
              <div className="usa-form-control">
                <Street
                  attribute={setAttribute(this.props.attribute, 'street2')}
                  name="street2"
                  className="street2"
                  label={this.props.street2Label}
                  optional
                  value={this.props.street2}
                  disabled={this.props.disabled}
                  onUpdate={this.onAddressUpdate}
                  onError={this.handleError}
                  onFocus={this.focusField}
                  onBlur={this.blurField}
                  isPoBoxAllowed={this.props.isPoBoxAllowed}
                  showDisabledText={this.props.showDisabledText}
                />
              </div>
              <div className="usa-form-control">
                <City
                  attribute={setAttribute(this.props.attribute, 'city')}
                  name="city"
                  className="city required"
                  label={this.props.cityLabel}
                  value={this.props.city}
                  onUpdate={this.onAddressUpdate}
                  onError={this.handleError}
                  onFocus={this.focusField}
                  onBlur={this.blurField}
                  required={this.props.required}
                  disabled={this.props.disabled}
                  showDisabledText={this.props.showDisabledText}
                />
              </div>
              <div className="state-zip-wrap">
                <State
                  attribute={setAttribute(this.props.attribute, 'state')}
                  name="state"
                  className="state required"
                  label={this.props.stateLabel}
                  value={this.props.state}
                  includeStates="true"
                  onUpdate={this.onAddressUpdate}
                  onError={this.handleError}
                  onFocus={this.focusField}
                  onBlur={this.blurField}
                  required={this.props.required}
                  disabled={this.props.disabled}
                />
                <ZipCode
                  attribute={setAttribute(this.props.attribute, 'zipcode')}
                  name="zipcode"
                  key="us_zipcode"
                  className="zipcode required"
                  label={this.props.zipcodeLabel}
                  value={this.props.zipcode}
                  status={instateZipcode}
                  onUpdate={this.onAddressUpdate}
                  onError={this.handleError}
                  onFocus={this.focusField}
                  onBlur={this.blurField}
                  required={this.props.required}
                  disabled={this.props.disabled}
                  showDisabledText={this.props.showDisabledText}
                />
              </div>
            </div>
          )}

          {this.props.isEnabled && isSafeInternational(locationObject, false) && (
            <>
              <div className="usa-form-control">
                <Street
                  attribute={setAttribute(this.props.attribute, 'street')}
                  name="street"
                  label={this.props.streetLabel}
                  placeholder={this.props.streetPlaceholder}
                  className="mailing street required"
                  value={this.props.street}
                  onUpdate={this.updateInternationalField}
                  onError={this.handleError}
                  onFocus={this.props.onFocus}
                  required={this.props.required}
                  disabled={this.props.disabled}
                  isPoBoxAllowed={this.props.isPoBoxAllowed}
                  showDisabledText={this.props.showDisabledText}
                />
              </div>
              <div className="usa-form-control">
                <Street
                  attribute={setAttribute(this.props.attribute, 'street2')}
                  name="street2"
                  className="street2"
                  label={this.props.street2Label}
                  optional
                  value={this.props.street2}
                  onUpdate={this.updateInternationalField}
                  onError={this.handleError}
                  onFocus={this.props.onFocus}
                  disabled={this.props.disabled}
                  isPoBoxAllowed={this.props.isPoBoxAllowed}
                  showDisabledText={this.props.showDisabledText}
                />
              </div>
              <div className="usa-form-control">
                <City
                  attribute={setAttribute(this.props.attribute, 'city')}
                  name="city"
                  className="city required"
                  label={this.props.cityLabel}
                  value={this.props.city}
                  onUpdate={this.updateInternationalField}
                  onError={this.handleError}
                  onFocus={this.props.onFocus}
                  required={this.props.required}
                  disabled={this.props.disabled}
                  showDisabledText={this.props.showDisabledText}
                />
              </div>
              <div className="usa-form-control">
                <Country
                  isPartOfLocationModel
                  attribute={setAttribute(this.props.attribute, 'country')}
                  name="country"
                  className="required"
                  label={this.props.countryLabel}
                  {...countryValueResolver(this.props)}
                  excludeUnitedStates="true"
                  onUpdate={this.updateCountry}
                  onError={this.handleError}
                  onFocus={this.props.onFocus}
                  required={this.props.required}
                  disabled={this.props.disabled}
                  updateCountryFocused={this.props.updateCountryFocused}
                />
              </div>
            </>
          )}

          {this.props.isEnabled && isSafePostOffice(locationObject, false) && (
            <div>
              <div className="usa-form-control">
                <Street
                  attribute={setAttribute(this.props.attribute, 'street')}
                  name="street"
                  label={i18n.t('address.apoFpo.street.label')}
                  placeholder={this.props.postOfficeStreetPlaceholder}
                  className="mailing street required"
                  value={this.props.street}
                  onUpdate={this.updateInternationalField}
                  onError={this.handleError}
                  onFocus={this.focusField}
                  required={this.props.required}
                  disabled={this.props.disabled}
                  isPoBoxAllowed={this.props.isPoBoxAllowed}
                  showDisabledText={this.props.showDisabledText}
                />
              </div>
              <div className="usa-form-control">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label id="apofpo">{i18n.t('address.apoFpo.select.label')}</label>
                <RadioGroup
                  attribute={setAttribute(this.props.attribute, 'city')}
                  className="apofpo option-list"
                  selectedValue={this.props.city}
                  disabled={this.props.disabled}
                  required={this.props.required}
                  onError={this.handleError}
                >
                  <Radio
                    name="city"
                    className="apo"
                    label={i18n.t('address.apoFpo.apoFpoType.apo.label')}
                    value="APO"
                    disabled={this.props.disabled}
                    onUpdate={this.onAddressUpdate}
                    onFocus={this.focusField}
                  />
                  <Radio
                    name="city"
                    className="fpo"
                    label={i18n.t('address.apoFpo.apoFpoType.fpo.label')}
                    value="FPO"
                    disabled={this.props.disabled}
                    onUpdate={this.onAddressUpdate}
                    onFocus={this.focusField}
                  />
                </RadioGroup>
              </div>
              <div className="state-zip-wrap">
                <ApoFpo
                  attribute={setAttribute(this.props.attribute, 'state')}
                  name="state"
                  className="state required"
                  label={this.props.postOfficeStateLabel}
                  value={this.props.state}
                  onUpdate={this.updateInternationalField}
                  onError={this.handleError}
                  onFocus={this.focusField}
                  onBlur={this.updateInternationalField}
                  required={this.props.required}
                  disabled={this.props.disabled}
                  tabNext={() => {
                    this.props.tab(
                      this.apoZipcode.current.zipcode.current.text.current.input.current
                    );
                  }}
                />
                <ZipCode
                  attribute={setAttribute(this.props.attribute, 'zipcode')}
                  name="zipcode"
                  ref={this.apoZipcode}
                  key="apo_zipcode"
                  className="zipcode required"
                  label={this.props.postOfficeZipcodeLabel}
                  value={this.props.zipcode}
                  status={instateZipcode}
                  onUpdate={this.updateInternationalField}
                  onError={this.handleError}
                  onFocus={this.focusField}
                  required={this.props.required}
                  disabled={this.props.disabled}
                  showDisabledText={this.props.showDisabledText}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

Address.defaultProps = {
  label: i18n.t('address.label'),
  tab: (input) => {
    input.focus();
  },
  country: { value: 'United States' },
  onFocus: () => { },
  onUpdate: () => { },
  onError: (value, arr) => arr,
  showPostOffice: false,
  isEnabled: true,
  streetLabel: i18n.t('address.us.street.label'),
  postOfficeStateLabel: i18n.t('address.apoFpo.apoFpo.label'),
  postOfficeZipcodeLabel: i18n.t('address.apoFpo.zipcode.label'),
  street2Label: i18n.t('address.us.street2.label'),
  confirmationWindowText: i18n.t('address.confirmation'),
  addressBooks: {},
  addressBook: '',
  isPoBoxAllowed: true,
  useChangeWarning: false,
  showDisabledText: false,
};

Address.errors = [
  {
    code: 'required',
    func: (value, props) => {
      if (props.required) {
        const country = countryString(props.country);
        switch (country) {
          case 'United States':
          case 'POSTOFFICE':
            return !!props.street && !!props.city && !!props.state && !!props.zipcode;
          default:
            return !!props.street && !!props.city && !!country;
        }
      }
      return true;
    },
  },
];
